import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  FILTER_SELECT_CLASS,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
  TABLE_HEADER_CELL_CLASS,
  TextButton,
} from '@safetyhub/ui';
import { extractApiError } from '../../lib/api';
import { useDirtyChildren, useReportDirty } from '../../hooks/useChildDirty';
import { useToastStore } from '../../stores/toast';
import {
  STORY_ACKNOWLEDGE_KIND_LABELS,
  STORY_ACKNOWLEDGE_KINDS,
  STORY_ACKNOWLEDGE_NOTE_MIN_LENGTH,
  STORY_CHECK_ENVS,
  storiesApi,
  type StoryAcknowledgeKind,
  type StoryCheckEnv,
  type StoryCheckStatus,
  type StoryLatestResult,
  type StoryViolation,
  type ViolationTally,
} from '../../lib/stories';
import StoryStatusBadge from './StoryStatusBadge';
import {
  ENV_TONE,
  formatRelative,
  parseCheckColumns,
  storyIdLabel,
  violationHeading,
} from './storyMonitorView';

/**
 * 스토리 상세 — **정의 + 판정 열 전문 + 위반 식별자 + 두 가지 쓰기**.
 *
 * ## 쓰기는 둘뿐이고 「실행」은 없다
 * `PATCH violations/:id/acknowledge`(큐에서만 뺀다)와 `POST :id/verify-result`(사람이 **이미 내린**
 * 판정의 기록). 불변식을 돌리는 엔드포인트는 **없다** — `RunnerGuide`의 docblock이 그 근거다.
 *
 * ## ⚠️ 중첩 모달을 쓰지 않는다
 * 확인 처리·수동 판정 폼은 이 모달 **안에서 펼친다**. 이 레포는 중첩 모달의 포털·z-index 사고를
 * 이미 치렀고(`memory/feedback-nested-modal-portal`), 층을 하나 더 쌓을 값어치가 없다.
 */
export interface StoryDetailModalProps {
  storyId: string;
  open: boolean;
  onClose: () => void;
  /** 목록 질의 키 — 쓰기 뒤 같은 키를 무효화해 화면의 수가 어긋나지 않게 한다 */
  listQueryKey: readonly unknown[];
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-bold uppercase tracking-wide text-ink-500">{label}</span>
      <span className="text-[13px] font-semibold text-ink-900">{children}</span>
    </div>
  );
}

/** run 메타 한 줄 — 「어느 기준의 수인가」. 이 칸이 없으면 부분 run의 「위반 0」이 전수로 읽힌다. */
function RunMeta({ run }: { run: StoryLatestResult['run'] }) {
  return (
    <span className="text-[11px] text-ink-500">
      <span className="inline-flex align-middle">
        <Badge tone={ENV_TONE[run.env] ?? 'slate'} size="sm">
          {run.env}
        </Badge>
      </span>{' '}
      {run.trigger} · 측정 {formatRelative(run.measuredAt)} ·{' '}
      {run.baseSha ? (
        <code>
          {run.baseSha.slice(0, 9)}
          {run.baseShaDirty ? ' (dirty)' : ''}
        </code>
      ) : (
        <span className="text-ink-400">기준 sha 없음</span>
      )}{' '}
      ·{' '}
      {run.specFilter.length === 0 ? (
        '전수'
      ) : (
        <span title={run.specFilter.join(', ')}>부분 run ({run.specFilter.length}개 명세)</span>
      )}
    </span>
  );
}

/** 판정 열 전문 — **대조군이 0이라 공허가 됐다**를 사람이 읽는 유일한 자리다. */
function CheckColumns({ columns }: { columns: unknown }) {
  const parsed = parseCheckColumns(columns);
  if (parsed === null) {
    return (
      <p className="text-[11.5px] text-amber-700">
        ⚠️ 판정 열의 형상이 낯설다 — 그려서 거짓을 보이는 대신 비워 둔다(「열이 없다」와 다른 사실이다).
      </p>
    );
  }
  if (parsed.length === 0) {
    return <p className="text-[11.5px] text-ink-500">판정 열이 없다.</p>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {parsed.map((column) => (
        <li key={column.col} className="text-[11.5px] leading-relaxed text-ink-700">
          <code className="font-bold">{column.col}</code> {column.must}
          {column.value !== null && (
            <>
              {' → '}
              <strong className={column.ok === false ? 'text-rose-600' : 'text-ink-900'}>
                {column.value}
                {column.unit ? ` ${column.unit}` : ''}
              </strong>
            </>
          )}
          {column.ok === false && <span className="ml-1 text-rose-600">✗</span>}
          {column.why && <span className="ml-1 text-ink-500">— {column.why}</span>}
        </li>
      ))}
    </ul>
  );
}

/** 확인 처리 폼 — **분류와 사유를 둘 다** 요구한다(어느 하나만이면 거짓 면제가 숨거나 근거가 사라진다). */
function AcknowledgeForm({
  violation,
  onDone,
  onDirtyChange,
}: {
  violation: StoryViolation;
  onDone: () => void;
  /** 미저장 닫기 보호 — 상세 창이 `isDirty`에 더한다([[useReportDirty]]). */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [kind, setKind] = useState<StoryAcknowledgeKind>(STORY_ACKNOWLEDGE_KINDS[0]);
  const [note, setNote] = useState('');
  const { showToast } = useToastStore();
  useReportDirty(kind !== STORY_ACKNOWLEDGE_KINDS[0] || note !== '', onDirtyChange);

  const mutation = useMutation({
    mutationFn: () => storiesApi.acknowledgeViolation(violation.id, { kind, note }),
    onSuccess: () => {
      showToast('확인 처리했습니다 — 큐에서만 빠지고 위반 수에는 계속 셉니다.', 'success');
      onDone();
    },
    onError: (error) => showToast(apiMessage(error), 'error'),
  });

  const tooShort = note.trim().length < STORY_ACKNOWLEDGE_NOTE_MIN_LENGTH;

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-card-sm bg-surface-soft p-3">
      <div>
        <label className={FORM_LABEL_CLASS} htmlFor={`ack-kind-${violation.id}`}>
          분류
        </label>
        <select
          id={`ack-kind-${violation.id}`}
          className={FILTER_SELECT_CLASS}
          value={kind}
          onChange={(event) => setKind(event.target.value as StoryAcknowledgeKind)}
        >
          {STORY_ACKNOWLEDGE_KINDS.map((option) => (
            <option key={option} value={option}>
              {STORY_ACKNOWLEDGE_KIND_LABELS[option].label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11.5px] leading-relaxed text-ink-500">
          {STORY_ACKNOWLEDGE_KIND_LABELS[kind].hint}
        </p>
      </div>
      <div>
        <label className={FORM_LABEL_CLASS} htmlFor={`ack-note-${violation.id}`}>
          사유 ({STORY_ACKNOWLEDGE_NOTE_MIN_LENGTH}자 이상) — ⚠️ 이름·연락처를 적지 말 것
        </label>
        <textarea
          id={`ack-note-${violation.id}`}
          className={FORM_TEXTAREA_CLASS}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="왜 이 건이 그 분류인가"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={tooShort || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? '처리 중…' : '확인 처리'}
        </Button>
        <TextButton onClick={onDone}>취소</TextButton>
        {tooShort && (
          <span className="text-[11.5px] text-ink-500">
            사유가 {STORY_ACKNOWLEDGE_NOTE_MIN_LENGTH}자 이상이어야 합니다.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * 수동 판정 기록 폼 — `POST :id/verify-result`.
 *
 * ⚠️ 「실행」이 아니라 **기록**이다. 사람(또는 Claude)이 `verify` 절차를 돌려 결과를 보고,
 * 그것을 **승인한 뒤** 남기는 흐름이 설계 ②의 장치다.
 * ⚠️ `measuredAt`은 **실제로 재 본 시각**이고 기록 시각(`judgedAt`)이 아니다 — 기본값으로 지금을
 * 채우되 사람이 고칠 수 있게 둔다.
 */
function ManualVerifyForm({
  storyId,
  onDone,
  onDirtyChange,
}: {
  storyId: string;
  onDone: () => void;
  /** 미저장 닫기 보호 — 상세 창이 `isDirty`에 더한다([[useReportDirty]]). */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [status, setStatus] = useState<StoryCheckStatus>('PASS');
  // 기본값 `prod`는 **정책**이고 집합의 사본이 아니다(prod 판정이 이 화면의 목적이다) — 그 값이
  // 집합에서 빠지면 `StoryCheckEnv`가 **tsc로** 잡으므로 여기 열거 경고를 달지 않는다.
  const [env, setEnv] = useState<StoryCheckEnv>('prod');
  const [measuredAt, setMeasuredAt] = useState(() => toLocalInputValue(new Date()));
  // 측정 시각의 기준선은 «폼을 연 시각» — 그 값을 고치지 않았으면 적던 내용이 아니다.
  const [initialMeasuredAt] = useState(measuredAt);
  const [detail, setDetail] = useState('');
  const { showToast } = useToastStore();
  useReportDirty(
    status !== 'PASS' || env !== 'prod' || measuredAt !== initialMeasuredAt || detail !== '',
    onDirtyChange,
  );

  const mutation = useMutation({
    mutationFn: () =>
      storiesApi.recordVerifyResult(storyId, {
        status,
        env,
        measuredAt: new Date(measuredAt).toISOString(),
        detail: detail.trim() === '' ? null : detail,
      }),
    onSuccess: () => {
      showToast('수동 판정을 기록했습니다.', 'success');
      onDone();
    },
    onError: (error) => showToast(apiMessage(error), 'error'),
  });

  const measuredAtInvalid = Number.isNaN(new Date(measuredAt).getTime());

  return (
    <div className="mt-2 flex flex-col gap-3 rounded-card-sm bg-surface-soft p-3">
      <p className="text-[11.5px] leading-relaxed text-ink-600">
        사람이 <strong>이미 내린</strong> 판정을 남깁니다 — 이 버튼이 불변식을 돌리지는 않습니다.
        근거(쓴 쿼리와 받은 숫자)를 함께 적어야 다음 사람이 그 초록을 신뢰할 수 있습니다.
      </p>
      <div className="flex flex-wrap gap-3">
        <div>
          <label className={FORM_LABEL_CLASS} htmlFor="manual-status">
            판정
          </label>
          <select
            id="manual-status"
            className={FILTER_SELECT_CLASS}
            value={status}
            onChange={(event) => setStatus(event.target.value as StoryCheckStatus)}
          >
            <option value="PASS">통과</option>
            <option value="VIOLATION">위반</option>
            <option value="VACUOUS">공허 (대조군이 0)</option>
            <option value="ERROR">측정 실패</option>
          </select>
        </div>
        <div>
          <label className={FORM_LABEL_CLASS} htmlFor="manual-env">
            어디서 판정했나
          </label>
          <select
            id="manual-env"
            className={FILTER_SELECT_CLASS}
            value={env}
            onChange={(event) => setEnv(event.target.value as StoryCheckEnv)}
          >
            {/* ⚠️ 손으로 열거하지 않는다 — 소유자는 shared `STORY_CHECK_ENVS`다. 이 자리는 필터
                드롭다운보다 **더 아프다**: 집합이 넓어졌는데 여기가 안 따라오면 사람이 그 env의
                판정을 **기록할 수 없다**(읽기가 아니라 쓰기 경로가 막힌다). tsc는 빠진
                `<option>`을 보지 못한다. */}
            {STORY_CHECK_ENVS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={FORM_LABEL_CLASS} htmlFor="manual-measured-at">
            실제로 재 본 시각
          </label>
          <input
            id="manual-measured-at"
            type="datetime-local"
            className={FORM_CONTROL_CLASS}
            value={measuredAt}
            onChange={(event) => setMeasuredAt(event.target.value)}
          />
        </div>
      </div>
      <div>
        <label className={FORM_LABEL_CLASS} htmlFor="manual-detail">
          판정 근거 — 쓴 쿼리와 받은 숫자. ⚠️ PII 금지(상세 화면에 그대로 나갑니다)
        </label>
        <textarea
          id="manual-detail"
          className={FORM_TEXTAREA_CLASS}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          placeholder={'SELECT count(*) FROM … WHERE …  → 0건\n[대조군] 같은 쿼리로 … → 12건'}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={measuredAtInvalid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? '기록 중…' : '판정 기록'}
        </Button>
        <TextButton onClick={onDone}>취소</TextButton>
        {measuredAtInvalid && (
          <span className="text-[11.5px] text-rose-600">측정 시각을 입력해 주세요.</span>
        )}
      </div>
    </div>
  );
}

/**
 * 「위반 건」 카드 머리 — **제목의 마커와 수가 같은 축(미해소)에서 온다.**
 *
 * 🔴 종전엔 두 축이 한 제목에 섞여 있었다: 마커는 `violationTally.open`에서, 숫자는
 * `violations.length`(**목록 길이** — 해소분 포함·서버 상한에 잘린다)에서. 그래서 해소만 7건인
 * 스토리가 **「위반 건 7」(🔴 없음)** 으로 보였다. 파생을 `violationHeading` 한 함수로 모아 마커와
 * 수가 다른 축을 가리킬 수 없게 했고, 목록 길이는 **보조 칸**으로 갈랐다.
 */
function ViolationCardHeader({
  tally,
  listLength,
}: {
  tally: ViolationTally;
  listLength: number;
}) {
  const heading = violationHeading(tally, listLength);
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className={CARD_SECTION_TITLE_CLASS}>
        위반 건 {heading.marker} {heading.count}
        <span className="ml-1 text-[11px] font-semibold text-ink-500">(미해소)</span>
      </h3>
      <span className="text-[11px] text-ink-500">
        미해소 {tally.open} (미확인 {tally.openUnacknowledged} · 확인 처리{' '}
        {tally.openAcknowledged}) · 해소 {tally.resolved}
        {heading.listNote !== null && (
          <>
            {' · '}
            <strong>{heading.listNote}</strong>
          </>
        )}
      </span>
    </div>
  );
}

export default function StoryDetailModal({
  storyId,
  open,
  onClose,
  listQueryKey,
}: StoryDetailModalProps) {
  const queryClient = useQueryClient();
  const [ackTarget, setAckTarget] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  // 미저장 닫기 보호 — 적던 내용은 하위 폼(확인 처리·수동 판정)의 state에 산다. 폼이 올려 준 dirty를
  // 모아 base `isDirty`로 넘긴다(폼이 닫히면 그 몫은 언마운트로 빠진다).
  const dirtyForms = useDirtyChildren();

  const detailQueryKey = ['admin', 'stories', 'detail', storyId] as const;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => storiesApi.get(storyId),
    enabled: open,
  });

  // 쓰기 뒤 **둘 다** 무효화한다 — 상세만 갱신하면 목록의 위반 수가 낡아 같은 화면 안에서 어긋난다
  const refreshBoth = () => {
    void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    void queryClient.invalidateQueries({ queryKey: listQueryKey });
  };

  return (
    <Modal open={open} onClose={onClose} size="xl" title={storyId} isDirty={dirtyForms.anyDirty}>
      {isLoading && <p className="text-sm text-ink-500">불러오는 중…</p>}
      {isError && <p className="text-sm text-rose-600">{apiMessage(error)}</p>}

      {data && (
        <div className="flex flex-col gap-4">
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className={CARD_SECTION_TITLE_CLASS}>{storyIdLabel(data.definition)}</h3>
              <StoryStatusBadge status={data.status} />
            </div>
            <p className="mt-2 text-[14px] font-bold text-ink-900">{data.definition.title}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-700">
              {data.definition.expectation}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-line pt-3 sm:grid-cols-4">
              <Field label="주체">{data.definition.actor}</Field>
              <Field label="트리거">
                {data.definition.triggers.length > 0 ? data.definition.triggers.join(' · ') : '—'}
              </Field>
              <Field label="verify 종류">{data.definition.verifyKind ?? '선언 없음'}</Field>
              <Field label="큐 제외 사유">
                {data.definition.offQueueKind ?? '—'}
                {data.definition.offQueueBy ? ` (${data.definition.offQueueBy})` : ''}
              </Field>
            </div>
            {data.definition.note && (
              <p className="mt-2 text-[11.5px] leading-relaxed text-ink-500">
                메모: {data.definition.note}
              </p>
            )}
            {data.definition.retiredAt && (
              <p className="mt-2 text-[11.5px] font-semibold text-amber-700">
                ⚠️ 은퇴한 스토리다 — 코드 레지스트리에서 사라졌고 결과·위반 이력 때문에 행만 남아 있다.
              </p>
            )}
          </Card>

          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className={CARD_SECTION_TITLE_CLASS}>
                불변식별 최신 판정 ({data.latestByInvariant.length}칸)
              </h3>
              <Button variant="secondary" size="sm" onClick={() => setManualOpen((v) => !v)}>
                {manualOpen ? '수동 판정 닫기' : '수동 판정 기록'}
              </Button>
            </div>
            <p className="mt-1 text-[11.5px] text-ink-500">
              그레인은 <strong>(스토리, 불변식, env)</strong>다 — 나중에 돈 dev 판정이 prod 위반을
              덮지 않는다. 스토리 상태는 이 칸들을 worst-of로 접은 것이다
              (위반 &gt; 측정 실패 &gt; 공허 &gt; 통과).
            </p>
            {manualOpen && (
              <ManualVerifyForm
                storyId={storyId}
                onDone={() => {
                  setManualOpen(false);
                  refreshBoth();
                }}
                onDirtyChange={(dirty) => dirtyForms.report('manual', dirty)}
              />
            )}
            {data.latestByInvariant.length === 0 ? (
              <p className="mt-3 text-[13px] font-semibold text-ink-600">
                판정 없음 — 아무도 재지 않았다. <strong>통과가 아니다.</strong>
              </p>
            ) : (
              <ul className="mt-3 flex flex-col gap-3">
                {data.latestByInvariant.map((result) => (
                  <li key={result.id} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <StoryStatusBadge status={result.status} size="sm" />
                      <code className="text-[12.5px] font-bold text-ink-900">
                        {result.invariantName ?? '(수동 판정)'}
                      </code>
                      <span className="inline-flex align-middle">
                        <Badge tone={result.source === 'MANUAL' ? 'violet' : 'blue'} size="sm">
                          {result.source === 'MANUAL' ? '사람이 기록' : '불변식'}
                        </Badge>
                      </span>
                      {result.violationCount > 0 && (
                        <span className="text-[12px] font-bold text-rose-600">
                          위반 {result.violationCount}
                        </span>
                      )}
                      <span className="text-[11px] text-ink-500">
                        판정 {formatRelative(result.checkedAt)}
                      </span>
                    </div>
                    <div className="mt-1">
                      <RunMeta run={result.run} />
                    </div>
                    <div className="mt-2">
                      <CheckColumns columns={result.columns} />
                    </div>
                    {result.detail && (
                      <p className="mt-1 whitespace-pre-wrap text-[11.5px] leading-relaxed text-ink-600">
                        {result.detail}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <ViolationCardHeader
              tally={data.violationTally}
              listLength={data.violations.length}
            />
            <p className="mt-1 text-[11.5px] text-ink-500">
              식별자만 싣는다(이름·연락처 없음). 확인 처리는 <strong>큐에서만</strong> 빼고 미해소
              수에는 계속 센다 — 면제가 아니다. <code>fixed</code>로 처리한 건이 다음 판정에 다시
              보이면 서버가 큐로 돌려보낸다. ⚠️ <strong>같은 대상이 env마다 한 행</strong>이다
              (유일 키에 env가 들어간다) — 같은 불변식·같은 식별자로 두 줄이 보이면 <strong>중복이
              아니라 dev·prod 각각의 관측</strong>이고, 앞의 배지가 그것을 가른다.
            </p>
            {data.violations.length < data.violationTally.open +
              data.violationTally.resolved && (
              <p className="mt-2 text-[11.5px] font-semibold text-amber-700">
                ⚠️ 서버가 내리는 위반 목록은 {data.violations.length}건에서 잘렸다 — 집계
                (미해소 {data.violationTally.open} · 해소 {data.violationTally.resolved})가 더 크다.
                아래 목록은 <strong>전수가 아니다</strong>.
              </p>
            )}
            {data.violations.length === 0 ? (
              <p className="mt-3 text-[13px] font-semibold text-emerald-700">위반 건 없음.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-[12.5px]">
                  <thead>
                    <tr>
                      <th className={TABLE_HEADER_CELL_CLASS}>불변식</th>
                      {/* 「환경 · 대상」 — env가 유일 키의 한 칸이라 **같은 대상이 env마다 한 행**이다 */}
                      <th className={TABLE_HEADER_CELL_CLASS}>환경 · 대상</th>
                      <th className={TABLE_HEADER_CELL_CLASS}>최초/최근 탐지</th>
                      <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
                      <th className={TABLE_HEADER_CELL_CLASS} />
                    </tr>
                  </thead>
                  <tbody>
                    {data.violations.map((violation) => (
                      <Fragment key={violation.id}>
                      <tr className="border-b border-line align-top">
                        <td className="px-4 py-2">
                          <code>{violation.invariantName}</code>
                        </td>
                        <td className="px-4 py-2">
                          {/* 🔑 env 배지 — 유일 키가 `(…, env)`라 **같은 entity가 dev·prod 2행으로
                              합법 존재한다.** 이 배지가 없으면 그 둘이 중복으로 읽힌다(같은 불변식·
                              같은 식별자로 두 줄이 보이므로). 색은 목록·run 기준 줄과 같은 `ENV_TONE`
                              이다 — dev 판정이 prod 판정처럼 읽히지 않게 하는 그 규약이다. */}
                          {/* ⚠️ 값이 없으면 **배지를 그리지 않는다** — 그 칸을 응답에 싣는 서버
                              변경과 이 화면이 **같은 run에 배포되지만 동시에는 아니다**(admin은
                              Cloudflare, api는 EC2). 그 창에서 `undefined`를 배지로 찍으면 화면이
                              거짓을 말하므로, 「아직 안 온다」는 **비워서** 보인다(이 화면이 404를
                              degrade하는 것과 같은 규율). */}
                          {violation.env && (
                            <>
                              <span className="inline-flex align-middle">
                                <Badge tone={ENV_TONE[violation.env] ?? 'slate'} size="sm">
                                  {violation.env}
                                </Badge>
                              </span>{' '}
                            </>
                          )}
                          <span className="text-ink-500">{violation.entityType}</span>{' '}
                          <code className="break-all">{violation.entityId}</code>
                        </td>
                        <td className="px-4 py-2 text-ink-600">
                          {formatRelative(violation.firstDetectedAt)} /{' '}
                          {formatRelative(violation.lastDetectedAt)}
                        </td>
                        <td className="px-4 py-2">
                          {violation.resolvedAt ? (
                            <span className="font-semibold text-emerald-700">해소</span>
                          ) : violation.acknowledgedAt ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="font-semibold text-amber-700">
                                확인 처리 ·{' '}
                                {violation.acknowledgeKind
                                  ? (STORY_ACKNOWLEDGE_KIND_LABELS[
                                      violation.acknowledgeKind as StoryAcknowledgeKind
                                    ]?.label ?? violation.acknowledgeKind)
                                  : '분류 없음'}
                              </span>
                              {violation.acknowledgeNote && (
                                <span className="text-[11px] text-ink-500">
                                  {violation.acknowledgeNote}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="font-semibold text-rose-600">🔴 미확인</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {!violation.resolvedAt && !violation.acknowledgedAt && (
                            <TextButton
                              onClick={() =>
                                setAckTarget((current) =>
                                  current === violation.id ? null : violation.id,
                                )
                              }
                            >
                              {ackTarget === violation.id ? '닫기' : '확인 처리'}
                            </TextButton>
                          )}
                        </td>
                      </tr>
                      {/* 폼은 **전체 폭 행**으로 내린다 — 좁은 액션 셀 안에 넣으면 분류 select와
                          사유 textarea가 눌린다(브라우저로 확인할 수 없는 자리라 안전한 쪽으로). */}
                      {ackTarget === violation.id && (
                        <tr className="border-b border-line">
                          <td colSpan={5} className="px-4 pb-3">
                            <AcknowledgeForm
                              violation={violation}
                              onDone={() => {
                                setAckTarget(null);
                                refreshBoth();
                              }}
                              onDirtyChange={(dirty) => dirtyForms.report(`ack:${violation.id}`, dirty)}
                            />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <h3 className={CARD_SECTION_TITLE_CLASS}>최근 판정 ({data.recentResults.length})</h3>
            <ul className="mt-2 flex flex-col gap-1.5">
              {data.recentResults.map((result) => (
                <li key={result.id} className="flex flex-wrap items-center gap-2 text-[11.5px]">
                  <StoryStatusBadge status={result.status} size="sm" />
                  <code>{result.invariantName ?? '(수동)'}</code>
                  <span className="inline-flex align-middle">
                    <Badge tone={ENV_TONE[result.run.env] ?? 'slate'} size="sm">
                      {result.run.env}
                    </Badge>
                  </span>
                  <span className="text-ink-500">
                    측정 {formatRelative(result.run.measuredAt)} · 기록{' '}
                    {formatRelative(result.run.judgedAt)}
                  </span>
                  {result.violationCount > 0 && (
                    <span className="font-bold text-rose-600">위반 {result.violationCount}</span>
                  )}
                </li>
              ))}
              {data.recentResults.length === 0 && (
                <li className="text-[12.5px] font-semibold text-ink-600">기록된 판정이 없다.</li>
              )}
            </ul>
          </Card>
        </div>
      )}
    </Modal>
  );
}

/**
 * 에러 문구 — axios raw가 사용자에게 뜨지 않게 한 겹 막는다.
 *
 * `extractApiError`는 응답 본문에 `error`가 없으면 `NETWORK_ERROR` + **axios의 영문 message**
 * (`Request failed with status code 400`)를 돌려준다. 그 문구가 화면에 뜨면 사용자는 무엇을
 * 고쳐야 할지 알 수 없으므로 그 갈래만 일반 문구로 바꾼다.
 */
function apiMessage(error: unknown): string {
  const parsed = extractApiError(error);
  if (parsed.code === 'NETWORK_ERROR' || parsed.code === 'UNKNOWN_ERROR') {
    return '요청을 처리할 수 없습니다. 잠시 뒤 다시 시도해 주세요.';
  }
  return parsed.message;
}

/** `datetime-local` 입력값(로컬 시각) — `toISOString()`은 UTC라 그대로 쓰면 9시간 어긋난다. */
function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}
